import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Baby,
  Cake,
  CalendarBlank,
  CaretLeft,
  CaretRight,
  Check,
  CheckCircle,
  ClockCountdown,
  PencilSimple,
  Question,
  UserCircle,
  UsersThree,
  WarningCircle,
} from '@phosphor-icons/react';

import {
  confirmActivityAttendance,
  confirmAttendance,
  getAttendanceGrid,
  reopenActivityAttendance,
  type AttendanceActivity,
  type AttendanceGrid,
  type AttendanceMember,
  type AttendanceRollup,
  type AttendanceStatus,
  type AttendanceSubsidy,
  type ConfirmAttendanceCell,
} from '@/api/attendance';
import { listTeams, type Team } from '@/api/teams';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import logoUrl from '@/assets/logo-sportadmin.svg';

// Attendance grid — leader-facing page for confirming who showed up at each
// activity, with subsidy-qualification "tells" once a column is confirmed.
//
// Two display modes per column:
//   • editing   — simple checkboxes. Tap to toggle present / not-present.
//                 No tri-state cycling; absent and unmarked render the
//                 same. Triggered by: (a) the column has unsaved local
//                 changes, (b) no records exist yet, or (c) the user
//                 explicitly clicked "Edit" on a confirmed column.
//   • confirmed — tells icons. Lime ✓ when the person counts toward the
//                 subsidy; outline glyph when present-but-ineligible (too
//                 young / too old / no DOB on file); muted dash when
//                 absent. Triggered when records exist AND no pending
//                 changes AND the org has subsidy rules configured.
//
// Each activity column has its own action pill in the header:
//   • dirty   → lime "Save" pill — saves just this column's diff.
//   • saved   → verdict chip (qualifies vs issues) + tiny "Edit" affordance.
//   • blank   → just the date/time, no verdict.
// The global "Save all" pill at the bottom of the filter row is a
// shortcut — same endpoint, all dirty cells in one request.

// Activities lock out attendance registration until they're within an
// hour of starting. Before that window, the column shows date + time but
// no checkboxes — leaders can't pre-fill attendance for an activity
// that hasn't started yet. After the window opens (and forever after),
// the column is editable.
const REGISTRATION_OPEN_MS = 60 * 60 * 1000;

type CellState = AttendanceStatus | '';
type ColumnMode = 'locked' | 'editing' | 'confirmed';

// CalendarColumn — the grid is an "advanced list" not a calendar: every
// activity in the visible month gets one column, packed in chronological
// order with no gaps for empty days. After the last activity we pad with
// muted `empty` columns up to a minimum count so a sparse month still
// looks like a real table instead of a tiny strip. When the same day
// holds multiple activities, showDate is false on the 2nd+ of the day so
// the date label only appears once across the cluster.
type CalendarColumn =
  | { kind: 'empty'; key: string }
  | {
      kind: 'activity';
      date: Date;
      activity: AttendanceActivity;
      showDate: boolean;
      key: string;
    };

// Layout geometry used to compute how many activity columns fit on one
// page without horizontal scrolling. Kept in px to match the measured
// container width. ACTIVITY_COL_PX is the minimum activity-column width
// (2.75rem); NAME_COL_PX is the sticky member-name column (14rem).
const ACTIVITY_COL_PX = 44;
const NAME_COL_PX = 224;
// Used before the container has been measured (first paint / SSR).
const FALLBACK_VISIBLE_COLUMNS = 14;

// The grid fetches a forward window of activities once, then paginates
// them client-side. 90 days stays under the API's 92-day range cap and
// covers the current month plus the next two — enough to page through.
const WINDOW_DAYS = 90;

// Background for "not an activity" columns/cells (padding + empty slots).
// A real grey mixed from the ink token over the page background — the
// `foreground/NN` opacity modifier doesn't render on this token (it's a
// bare CSS var), so we mix explicitly the way the rest of the SPA does.
const INACTIVE_BG =
  'bg-[color-mix(in_oklch,var(--color-fg)_4%,var(--color-bg))]';

interface PendingMap {
  [key: string]: CellState; // key = `${activityId}__${memberId}`
}

const cellKey = (activityId: string, memberId: string) =>
  `${activityId}__${memberId}`;

// In the simplified UI, every click toggles between present and clear.
// Absent and excused remain valid states in the data model — they're set
// by other paths (AI proposals, bulk import) — but the leader-facing grid
// treats absent / excused / clear identically as "not present".
function toggleState(current: CellState): CellState {
  return current === 'present' ? '' : 'present';
}

// buildBaseline turns the server-side records into the same map shape the
// pending edits live in, so diffing is a simple per-key compare.
function buildBaseline(grid: AttendanceGrid | undefined): PendingMap {
  const out: PendingMap = {};
  if (!grid) return out;
  for (const r of grid.records) {
    out[cellKey(r.activity_id, r.member_id)] = r.status;
  }
  return out;
}

export function Attendance() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t, i18n } = useTranslation('common');
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug,
  });
  const teams = teamsQuery.data?.teams ?? [];

  const urlTeam = searchParams.get('team') ?? '';
  const selectedTeam: Team | undefined =
    teams.find((tm) => tm.id === urlTeam) ?? teams[0];

  // We fetch one forward window of activities (current month start →
  // +WINDOW_DAYS) and paginate them client-side. Navigation steps pages,
  // not months: each page shows as many activity columns as fit the
  // container width, the rest padded with grey. from/to are stable for
  // the session so the query + scope key don't churn as the user pages.
  const today = useMemo(() => new Date(), []);
  const { from, to } = useMemo(() => windowRange(today), [today]);
  const fromDate = useMemo(() => parseISODate(from), [from]);
  const toDate = useMemo(() => parseISODate(to), [to]);

  // Measure the table area so we can fit exactly as many activity
  // columns as the width allows (no horizontal scroll). Recomputed on
  // resize via ResizeObserver.
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const visibleColumns = useVisibleColumns(tableWrapRef);

  const [pageOffset, setPageOffset] = useState(0);

  const gridQuery = useQuery({
    queryKey: ['attendance-grid', slug, selectedTeam?.id, from, to],
    queryFn: () =>
      getAttendanceGrid(token, slug, selectedTeam!.id, fromDate!, toDate!),
    enabled:
      !!token && !!slug && !!selectedTeam && !!fromDate && !!toDate,
  });
  const grid = gridQuery.data;

  const [pending, setPending] = useState<PendingMap>({});
  const baseline = useMemo(() => buildBaseline(grid), [grid]);
  const merged = useMemo<PendingMap>(
    () => ({ ...baseline, ...pending }),
    [baseline, pending],
  );

  // Reset the optimistic overlay whenever the scope (team / window) changes.
  const scopeKey = `${selectedTeam?.id ?? ''}|${from}|${to}`;
  const [lastScopeKey, setLastScopeKey] = useState(scopeKey);
  if (lastScopeKey !== scopeKey) {
    setLastScopeKey(scopeKey);
    setPending({});
  }

  const invalidateGrid = useCallback(
    () =>
      qc.invalidateQueries({
        queryKey: ['attendance-grid', slug, selectedTeam?.id, from, to],
      }),
    [qc, slug, selectedTeam?.id, from, to],
  );

  // saveCells persists a batch of cell marks. Cells auto-save on every
  // checkbox click (single-cell batch) and on mark-all (full-column batch),
  // so there is no separate save step — the grid is always current.
  const saveCells = useMutation({
    mutationFn: async (cells: ConfirmAttendanceCell[]) => {
      if (!selectedTeam) throw new Error('no team selected');
      return confirmAttendance(token, slug, selectedTeam.id, cells);
    },
    onSuccess: () => {
      void invalidateGrid();
    },
  });

  // Activity-level confirm (finalise + lock for LOK) and reopen. Cells are
  // already saved by the time the user confirms, so confirm is purely the
  // lock; reopen clears it so the column's cells become editable again.
  const confirmCol = useMutation({
    mutationFn: async (activityId: string) => {
      if (!selectedTeam) throw new Error('no team selected');
      return confirmActivityAttendance(token, slug, selectedTeam.id, activityId);
    },
    onSuccess: () => {
      void invalidateGrid();
    },
  });
  const reopenCol = useMutation({
    mutationFn: async (activityId: string) => {
      if (!selectedTeam) throw new Error('no team selected');
      return reopenActivityAttendance(token, slug, selectedTeam.id, activityId);
    },
    onSuccess: () => {
      void invalidateGrid();
    },
  });

  // Every checkbox click auto-saves that one cell immediately (optimistic
  // overlay + single-cell batch save) — no separate save step.
  const onCellClick = useCallback(
    (activityId: string, memberId: string) => {
      const key = cellKey(activityId, memberId);
      const next = toggleState(merged[key] ?? '');
      setPending((prev) => ({ ...prev, [key]: next }));
      saveCells.mutate([
        { activity_id: activityId, member_id: memberId, status: next },
      ]);
    },
    [merged, saveCells],
  );

  // Confirm checkbox in the column header: tick → confirm (lock for LOK),
  // untick → reopen. Cells are already saved, so confirm doesn't batch.
  const onColumnConfirmToggle = useCallback(
    (activityId: string, checked: boolean) => {
      if (checked) confirmCol.mutate(activityId);
      else reopenCol.mutate(activityId);
    },
    [confirmCol, reopenCol],
  );

  // Mark-all toggles every athlete + leader present (or clears them all)
  // for one column, auto-saving the whole batch.
  const onColumnMarkAll = useCallback(
    (activityId: string, checked: boolean) => {
      const members = [...(grid?.athletes ?? []), ...(grid?.leaders ?? [])];
      const next: CellState = checked ? 'present' : '';
      setPending((prev) => {
        const m = { ...prev };
        for (const mem of members) m[cellKey(activityId, mem.id)] = next;
        return m;
      });
      saveCells.mutate(
        members.map((mem) => ({
          activity_id: activityId,
          member_id: mem.id,
          status: next,
        })),
      );
    },
    [grid, saveCells],
  );

  const onTeamChange = useCallback(
    (teamId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('team', teamId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const rules = grid?.subsidy;

  // Activities sorted once, then paginated into pages of `visibleColumns`.
  const sortedActivities = useMemo(
    () =>
      [...(grid?.activities ?? [])].sort(
        (a, b) =>
          new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
      ),
    [grid?.activities],
  );
  const totalPages = Math.max(
    1,
    Math.ceil(sortedActivities.length / visibleColumns),
  );
  // Clamp the page if the activity count or column width shrank under it.
  const safePage = Math.min(pageOffset, totalPages - 1);
  if (safePage !== pageOffset) {
    setPageOffset(safePage);
  }

  const onPrevPage = useCallback(() => {
    setPageOffset((p) => Math.max(0, p - 1));
  }, []);
  const onNextPage = useCallback(() => {
    setPageOffset((p) => p + 1);
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:gap-6 md:p-8">
      <PageHeader
        eyebrow={t('attendance.eyebrow')}
        title={t('attendance.title')}
      />

      <FilterRow
        teams={teams}
        selectedTeam={selectedTeam}
        onTeamChange={onTeamChange}
        page={safePage}
        totalPages={totalPages}
        onPrevPage={onPrevPage}
        onNextPage={onNextPage}
        t={t}
      />

      <div ref={tableWrapRef}>
        {!selectedTeam ? (
          <EmptyState
            icon={<UsersThree weight="duotone" />}
            title={t('attendance.empty.noTeams.title')}
            body={t('attendance.empty.noTeams.body')}
            actions={[
              {
                label: t('attendance.empty.noTeams.cta'),
                to: `/orgs/${slug}/teams`,
                icon: <UsersThree weight="bold" />,
              },
            ]}
          />
        ) : gridQuery.isLoading ? (
          <EmptyState
            icon={<CalendarBlank weight="duotone" />}
            title={t('attendance.loading')}
            loading
          />
        ) : grid && grid.activities.length === 0 ? (
          <EmptyState
            icon={<CalendarBlank weight="duotone" />}
            title={t('attendance.empty.noActivities.title')}
            body={t('attendance.empty.noActivities.body')}
          />
        ) : grid ? (
          <AttendanceTable
            grid={grid}
            sortedActivities={sortedActivities}
            page={safePage}
            visibleColumns={visibleColumns}
            merged={merged}
            now={today}
            rules={rules}
            onCellClick={onCellClick}
            onColumnConfirmToggle={onColumnConfirmToggle}
            onColumnMarkAll={onColumnMarkAll}
            savingColumn={
              new Set<string>([
                ...(confirmCol.isPending && confirmCol.variables
                  ? [confirmCol.variables]
                  : []),
                ...(reopenCol.isPending && reopenCol.variables
                  ? [reopenCol.variables]
                  : []),
              ])
            }
            locale={i18n.language}
            t={t}
          />
        ) : null}
      </div>
    </div>
  );
}

// useVisibleColumns measures the table wrapper and returns how many
// activity columns fit after the sticky name column, so a page never
// needs horizontal scrolling. Falls back to a sane default until the
// first measurement lands.
function useVisibleColumns(ref: React.RefObject<HTMLElement | null>): number {
  const [count, setCount] = useState(FALLBACK_VISIBLE_COLUMNS);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (width: number) => {
      const fit = Math.floor((width - NAME_COL_PX) / ACTIVITY_COL_PX);
      setCount(Math.max(1, fit));
    };
    measure(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) measure(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return count;
}

// ============================================================
// Header + filters
// ============================================================

function PageHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  // Compact on mobile: small eyebrow, single-line title, no subtitle — the
  // page's purpose is "check boxes" and the help banner below carries the
  // longer explanation when needed.
  return (
    <header className="flex flex-col gap-1">
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
        {eyebrow}
      </p>
      <h1 className="font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
        {title}
      </h1>
    </header>
  );
}

interface FilterRowProps {
  teams: Team[];
  selectedTeam: Team | undefined;
  onTeamChange: (id: string) => void;
  page: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  t: TFunction<'common'>;
}

function FilterRow({
  teams,
  selectedTeam,
  onTeamChange,
  page,
  totalPages,
  onPrevPage,
  onNextPage,
  t,
}: FilterRowProps) {
  // Team selector on the left, page pager on the right — one line on
  // md+, stacked on mobile.
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
      <label className="flex min-w-0 flex-col gap-1.5 md:w-64">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
          {t('attendance.filters.team')}
        </span>
        <Select
          value={selectedTeam?.id ?? ''}
          onValueChange={onTeamChange}
          disabled={teams.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('attendance.filters.teamPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      <Pager
        page={page}
        totalPages={totalPages}
        onPrev={onPrevPage}
        onNext={onNextPage}
        t={t}
      />
    </div>
  );
}

function Pager({
  page,
  totalPages,
  onPrev,
  onNext,
  t,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  t: TFunction<'common'>;
}) {
  // Page navigation through the activity list. The month context lives
  // in the table's month-band header, so the pager just reports position.
  const atStart = page <= 0;
  const atEnd = page >= totalPages - 1;
  return (
    <div className="inline-flex items-center gap-1 self-start rounded-pill border border-border bg-card px-1 py-1 shadow-xs md:self-auto">
      <button
        type="button"
        onClick={onPrev}
        disabled={atStart}
        aria-label={t('attendance.page.prev')}
        className="inline-flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
      >
        <CaretLeft weight="bold" className="size-4" />
      </button>
      <span className="min-w-[7rem] px-2 text-center text-sm font-semibold tabular-nums text-foreground">
        {t('attendance.page.indicator', {
          current: page + 1,
          total: totalPages,
        })}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={atEnd}
        aria-label={t('attendance.page.next')}
        className="inline-flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
      >
        <CaretRight weight="bold" className="size-4" />
      </button>
    </div>
  );
}

// ============================================================
// Grid
// ============================================================

interface AttendanceTableProps {
  grid: AttendanceGrid;
  sortedActivities: AttendanceActivity[];
  page: number;
  visibleColumns: number;
  merged: PendingMap;
  now: Date;
  rules: AttendanceSubsidy | undefined;
  onCellClick: (activityId: string, memberId: string) => void;
  onColumnConfirmToggle: (activityId: string, checked: boolean) => void;
  onColumnMarkAll: (activityId: string, checked: boolean) => void;
  savingColumn: Set<string>;
  locale: string;
  t: TFunction<'common'>;
}

function AttendanceTable({
  grid,
  sortedActivities,
  page,
  visibleColumns,
  merged,
  now,
  rules,
  onCellClick,
  onColumnConfirmToggle,
  onColumnMarkAll,
  savingColumn,
  locale,
  t,
}: AttendanceTableProps) {
  // Weekday "Mån" + day-of-month "12" + time "17:30" stack tightly in the
  // narrow column header. 24h time per CLAUDE.md. All formatters are
  // locale-aware so en/sv/no/da/fi render their own short weekday.
  const weekdayFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: 'short' }),
    [locale],
  );
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric' }),
    [locale],
  );

  // Index rollups by id for O(1) lookup per column.
  const rollupByActivity = useMemo(() => {
    const m = new Map<string, AttendanceRollup>();
    for (const r of grid.rollups ?? []) m.set(r.activity_id, r);
    return m;
  }, [grid.rollups]);

  const rulesConfigured = !!rules && rules.kind !== '';

  // Build this page's columns: the page slice of activities packed left,
  // then grey padding out to visibleColumns, plus the month-band row.
  const calendarColumns = useMemo(
    () => buildCalendarColumns(sortedActivities, page, visibleColumns),
    [sortedActivities, page, visibleColumns],
  );
  const monthBands = useMemo(
    () => buildMonthBands(calendarColumns, locale),
    [calendarColumns, locale],
  );

  // Activity columns flex to fill the measured width (min 2.75rem) so the
  // page always fills the container edge-to-edge without horizontal
  // scroll, regardless of how many columns fit.
  const cols = `14rem repeat(${calendarColumns.length}, minmax(2.75rem, 1fr))`;

  // Eligible counts for section headers — "X eligible / Y total" so the
  // leader knows up front how many on the roster could qualify even when
  // no records exist yet.
  const eligibleAthletes = rulesConfigured
    ? grid.athletes.filter((m) => isAthleteEligibleToday(m, rules))
        .length
    : grid.athletes.length;
  const eligibleLeaders = rulesConfigured
    ? grid.leaders.filter((m) => isLeaderEligibleToday(m, rules)).length
    : grid.leaders.length;

  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-xs">
      <div
        className="w-full"
        style={{ display: 'grid', gridTemplateColumns: cols }}
      >
        {/* Top-left corner — spans both header rows, holds the logo. */}
        <HeaderCornerCell />

        {/* Month band row — spans groups of activity columns by month. */}
        {monthBands.map((b) => (
          <MonthBandCell key={b.key} label={b.label} span={b.span} />
        ))}

        {calendarColumns.map((col) => {
          if (col.kind === 'empty') {
            return <EmptyColumnHeader key={col.key} />;
          }
          const a = col.activity;
          const mode = computeColumnMode(a, now, a.confirmed_at ?? null);
          const rollup = rollupByActivity.get(a.id);
          return (
            <ActivityHeader
              key={col.key}
              activity={a}
              date={col.date}
              showDate={col.showDate}
              weekdayFmt={weekdayFmt}
              dayFmt={dayFmt}
              mode={mode}
              saving={savingColumn.has(a.id)}
              rulesConfigured={rulesConfigured}
              rollup={rollup}
              onConfirmToggle={(checked) => onColumnConfirmToggle(a.id, checked)}
              t={t}
            />
          );
        })}

        {/* Athletes */}
        <SectionRow
          label={t('attendance.sections.members')}
          subtitle={
            rulesConfigured
              ? t('attendance.sections.eligibleOf', {
                  eligible: eligibleAthletes,
                  total: grid.athletes.length,
                })
              : t('attendance.sections.totalOnly', {
                  count: grid.athletes.length,
                })
          }
          colSpan={calendarColumns.length + 1}
        />
        {grid.athletes.length === 0 ? (
          <EmptyRosterRow
            label={t('attendance.empty.noRoster')}
            colSpan={calendarColumns.length + 1}
          />
        ) : (
          grid.athletes.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              role="athlete"
              columns={calendarColumns}
              merged={merged}
              now={now}
              rules={rules}
              onCellClick={onCellClick}
              t={t}
            />
          ))
        )}

        {/* Leaders */}
        <SectionRow
          label={t('attendance.sections.leaders')}
          subtitle={
            rulesConfigured
              ? t('attendance.sections.eligibleOf', {
                  eligible: eligibleLeaders,
                  total: grid.leaders.length,
                })
              : t('attendance.sections.totalOnly', {
                  count: grid.leaders.length,
                })
          }
          colSpan={calendarColumns.length + 1}
        />
        {grid.leaders.length === 0 ? (
          <EmptyRosterRow
            label={t('attendance.empty.noRoster')}
            colSpan={calendarColumns.length + 1}
          />
        ) : (
          grid.leaders.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              role="leader"
              columns={calendarColumns}
              merged={merged}
              now={now}
              rules={rules}
              onCellClick={onCellClick}
              t={t}
            />
          ))
        )}

        {/* Footer summary rows: mark-all, present counts, edit link. */}
        <TableFooter
          columns={calendarColumns}
          athletes={grid.athletes}
          leaders={grid.leaders}
          merged={merged}
          now={now}
          onMarkAll={onColumnMarkAll}
          t={t}
        />
      </div>
    </div>
  );
}

interface TableFooterProps {
  columns: CalendarColumn[];
  athletes: AttendanceMember[];
  leaders: AttendanceMember[];
  merged: PendingMap;
  now: Date;
  onMarkAll: (activityId: string, checked: boolean) => void;
  t: TFunction<'common'>;
}

// TableFooter renders the four summary rows under the roster: a per-
// column "mark all present" toggle, present-count rows for members and
// leaders, and a per-activity edit link. Padding columns stay grey.
function TableFooter({
  columns,
  athletes,
  leaders,
  merged,
  now,
  onMarkAll,
  t,
}: TableFooterProps) {
  const presentCount = (activityId: string, members: AttendanceMember[]) =>
    members.filter((m) => merged[cellKey(activityId, m.id)] === 'present')
      .length;

  return (
    <>
      {/* Mark all */}
      <FooterRowShell label={t('attendance.footer.markAll')}>
        {columns.map((col) => {
          if (col.kind === 'empty') {
            return <FooterPadCell key={col.key} />;
          }
          const a = col.activity;
          const mode = computeColumnMode(a, now, a.confirmed_at ?? null);
          const roster = [...athletes, ...leaders];
          const allPresent =
            roster.length > 0 &&
            roster.every((m) => merged[cellKey(a.id, m.id)] === 'present');
          return (
            <div
              key={col.key}
              className="flex min-h-[2.5rem] items-center justify-center border-b border-l border-border bg-card"
            >
              {mode === 'editing' ? (
                <button
                  type="button"
                  onClick={() => onMarkAll(a.id, !allPresent)}
                  aria-pressed={allPresent}
                  aria-label={t('attendance.footer.markAll')}
                  className={cn(
                    'inline-flex size-5 items-center justify-center rounded-md border transition-colors',
                    allPresent
                      ? 'border-primary bg-primary text-primary-foreground shadow-xs'
                      : 'border-border bg-card text-foreground hover:bg-muted',
                  )}
                >
                  {allPresent ? <Check className="size-3.5" weight="bold" /> : null}
                </button>
              ) : (
                <span className="inline-block size-5" aria-hidden />
              )}
            </div>
          );
        })}
      </FooterRowShell>

      {/* Members present count */}
      <FooterRowShell label={t('attendance.footer.members')}>
        {columns.map((col) =>
          col.kind === 'empty' ? (
            <FooterPadCell key={col.key} />
          ) : (
            <FooterCountCell
              key={col.key}
              count={presentCount(col.activity.id, athletes)}
            />
          ),
        )}
      </FooterRowShell>

      {/* Leaders present count */}
      <FooterRowShell label={t('attendance.footer.leaders')}>
        {columns.map((col) =>
          col.kind === 'empty' ? (
            <FooterPadCell key={col.key} />
          ) : (
            <FooterCountCell
              key={col.key}
              count={presentCount(col.activity.id, leaders)}
            />
          ),
        )}
      </FooterRowShell>

      {/* Edit activity */}
      <FooterRowShell label={t('attendance.footer.editActivity')}>
        {columns.map((col) =>
          col.kind === 'empty' ? (
            <FooterPadCell key={col.key} />
          ) : (
            <div
              key={col.key}
              className="flex min-h-[2.5rem] items-center justify-center border-b border-l border-border bg-card"
            >
              <Link
                to="../schedule"
                aria-label={t('attendance.footer.editActivity')}
                title={t('attendance.footer.editActivity')}
                className="inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <PencilSimple className="size-4" weight="bold" />
              </Link>
            </div>
          ),
        )}
      </FooterRowShell>
    </>
  );
}

// Shared shell for a footer row: sticky label cell on the left, then the
// per-column cells supplied as children.
function FooterRowShell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="sticky left-0 z-10 flex items-center justify-end border-b border-border bg-muted/40 px-3 py-2 text-right text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </>
  );
}

function FooterPadCell() {
  return (
    <div
      className={cn('min-h-[2.5rem] border-b border-l border-border', INACTIVE_BG)}
      aria-hidden
    />
  );
}

function FooterCountCell({ count }: { count: number }) {
  return (
    <div className="flex min-h-[2.5rem] items-center justify-center border-b border-l border-border bg-card text-[0.8125rem] font-semibold tabular-nums text-foreground/70">
      {count}
    </div>
  );
}

// ============================================================
// Header cells
// ============================================================

// HeaderCornerCell spans both header rows (month band + column header)
// above the member-name column and carries the SportAdmin wordmark.
function HeaderCornerCell() {
  return (
    <div
      className="sticky left-0 z-10 flex items-center justify-center border-b border-border bg-muted/60 px-4"
      style={{ gridRow: 'span 2' }}
    >
      <img src={logoUrl} alt="" className="h-6 w-auto opacity-80 md:h-7" />
    </div>
  );
}

function MonthBandCell({ label, span }: { label: string; span: number }) {
  // Padding bands carry an empty label — render as inert grey space so
  // they read as "table continues here" without competing for attention.
  return (
    <div
      className={cn(
        'border-b border-l border-border px-1.5 py-1 text-center text-[0.625rem] font-bold uppercase tracking-wider',
        label
          ? 'bg-muted/40 text-muted-foreground'
          : cn(INACTIVE_BG, 'text-transparent'),
      )}
      style={{ gridColumn: `span ${span} / span ${span}` }}
    >
      {label || ' '}
    </div>
  );
}

// EmptyColumnHeader — a padding column at the tail of the table, used
// to keep the grid looking full when the month has fewer activities
// than MIN_VISIBLE_COLUMNS. Greyed so it reads as inert background.
function EmptyColumnHeader() {
  return (
    <div
      className={cn('border-b border-l border-border', INACTIVE_BG)}
      aria-hidden
    />
  );
}

interface ActivityHeaderProps {
  activity: AttendanceActivity;
  date: Date;
  showDate: boolean;
  weekdayFmt: Intl.DateTimeFormat;
  dayFmt: Intl.DateTimeFormat;
  mode: ColumnMode;
  saving: boolean;
  rulesConfigured: boolean;
  rollup: AttendanceRollup | undefined;
  onConfirmToggle: (checked: boolean) => void;
  t: TFunction<'common'>;
}

function ActivityHeader({
  activity,
  date,
  showDate,
  weekdayFmt,
  dayFmt,
  mode,
  saving,
  rulesConfigured,
  rollup,
  onConfirmToggle,
  t,
}: ActivityHeaderProps) {
  const [open, setOpen] = useState(false);
  const start = new Date(activity.starts_at);
  const verdictKind = computeColumnVerdict(rollup, mode === 'confirmed');

  // Time as "17⁰⁰": hours render at body size, minutes render smaller
  // and baseline-aligned to the top so they read like a superscript —
  // visually quieter than a full "17:30" without losing the minute info.
  const hours = String(start.getHours()).padStart(2, '0');
  const minutes = String(start.getMinutes()).padStart(2, '0');

  // Tint the whole header lime when confirmed + qualifies and red when
  // confirmed + issues so verdict reads at a glance. Other states stay
  // transparent — the row dividers carry the structure.
  const verdictTint =
    mode === 'confirmed' && verdictKind === 'qualifies'
      ? 'bg-primary/10'
      : mode === 'confirmed' && verdictKind === 'issues'
        ? 'bg-destructive/10'
        : '';

  return (
    <div
      className={cn(
        'relative flex flex-col items-stretch divide-y divide-border border-b border-l border-border text-center',
        verdictTint,
      )}
    >
      {/* Day section: weekday + day number. Suppressed on the 2nd+ sub-
          column of a multi-activity day, but the slot stays reserved so
          the time + checkbox rows line up across the cluster. */}
      <div className="flex flex-col items-center gap-0.5 px-0.5 py-1">
        {showDate ? (
          <>
            <span className="text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground">
              {weekdayFmt.format(date)}
            </span>
            <span className="text-[0.8125rem] font-semibold tabular-nums text-foreground/70">
              {dayFmt.format(date)}
            </span>
          </>
        ) : (
          <>
            <span className="invisible text-[0.625rem]" aria-hidden>
              ·
            </span>
            <span className="invisible text-[0.8125rem]" aria-hidden>
              ·
            </span>
          </>
        )}
      </div>

      {/* Time section: superscript minutes. */}
      <div className="flex items-start justify-center px-0.5 py-1 tabular-nums text-muted-foreground">
        <span className="text-[0.8125rem] font-semibold leading-none">
          {hours}
        </span>
        <span className="text-[0.5rem] font-semibold leading-none">
          {minutes}
        </span>
      </div>

      {/* Confirm section. */}
      <div className="flex items-center justify-center px-0.5 py-1.5">
        {mode === 'locked' ? (
          // Empty slot keeps row alignment with non-locked columns; the
          // absence of a checkbox is itself the "not open yet" signal.
          <span className="inline-block size-5" aria-hidden />
        ) : (
          <button
            type="button"
            onClick={() => onConfirmToggle(!(mode === 'confirmed'))}
            disabled={saving}
            aria-pressed={mode === 'confirmed'}
            aria-label={t('attendance.column.confirmCheckbox')}
            title={
              mode === 'confirmed'
                ? t('attendance.column.edit')
                : t('attendance.column.confirm')
            }
            className={cn(
              'inline-flex size-5 items-center justify-center rounded-md border transition-colors',
              mode === 'confirmed'
                ? 'border-primary bg-primary text-primary-foreground shadow-xs'
                : 'border-border bg-card text-foreground hover:bg-muted',
              saving && 'opacity-60',
            )}
          >
            {mode === 'confirmed' ? (
              <Check className="size-3.5" weight="bold" />
            ) : null}
          </button>
        )}
      </div>

      {/* Verdict section — reserved whenever rules are configured so the
          header height stays constant across confirmed / unconfirmed
          columns (otherwise the corner logo jumps as cells confirm). The
          icon only appears once confirmed; otherwise the slot is blank. */}
      {rulesConfigured ? (
        <div className="flex items-center justify-center px-0.5 py-1">
          {mode === 'confirmed' && rollup ? (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={cn(
                'inline-flex size-4 items-center justify-center rounded-full',
                verdictKind === 'qualifies' && 'text-primary',
                verdictKind === 'issues' && 'text-destructive',
                verdictKind === 'blank' && 'text-muted-foreground',
              )}
              aria-label={t('attendance.column.verdictAria')}
            >
              <VerdictIcon kind={verdictKind} className="size-4" />
            </button>
          ) : (
            <span className="inline-block size-4" aria-hidden />
          )}
        </div>
      ) : null}

      {/* Issue breakdown — expanded by tap on the verdict pill. Renders
          underneath the header so it doesn't break the grid layout. */}
      {open && rollup ? (
        <div className="absolute left-0 z-30 mt-1 w-56 rounded-xl border border-border bg-card p-2 text-left text-[0.75rem] shadow-lg" style={{ top: '100%' }}>
          <p className="font-semibold text-foreground">
            {t('attendance.column.breakdownTitle')}
          </p>
          <ul className="mt-1 space-y-1 text-muted-foreground">
            <li>
              {t('attendance.column.duration', {
                minutes: rollup.duration_minutes,
              })}
            </li>
            <li>
              {t('attendance.column.eligibleAthletes', {
                count: rollup.eligible_athletes_present,
              })}
            </li>
            <li>
              {t('attendance.column.eligibleLeaders', {
                count: rollup.eligible_leaders_present,
              })}
            </li>
            {(rollup.issues ?? []).map((issue) => (
              <li
                key={issue}
                className="inline-flex items-center gap-1 text-destructive"
              >
                <IssueIcon issue={issue} className="size-3.5" />
                {t(`attendance.issues.${issue}`, { count: 1 })}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex w-full justify-center rounded-pill border border-border bg-muted px-3 py-1 text-[0.6875rem] font-semibold text-foreground"
          >
            {t('attendance.column.close')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function VerdictIcon({
  kind,
  className,
}: {
  kind: 'qualifies' | 'issues' | 'blank';
  className?: string;
}) {
  if (kind === 'qualifies') {
    return <CheckCircle weight="fill" className={cn(className, 'text-primary')} />;
  }
  if (kind === 'issues') {
    return <WarningCircle weight="fill" className={cn(className, 'text-destructive')} />;
  }
  return <span className={cn(className, 'block rounded-full bg-border')} />;
}

function SectionRow({
  label,
  subtitle,
  colSpan,
}: {
  label: string;
  subtitle?: string;
  colSpan: number;
}) {
  return (
    <div
      className="sticky left-0 z-10 col-span-full flex items-baseline justify-between border-b border-border bg-muted/60 px-3 py-2"
      style={{ gridColumn: `1 / span ${colSpan}` }}
    >
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-foreground/80">
        {label}
      </span>
      {subtitle ? (
        <span className="text-[0.6875rem] tabular-nums text-muted-foreground">
          {subtitle}
        </span>
      ) : null}
    </div>
  );
}

function EmptyRosterRow({
  label,
  colSpan,
}: {
  label: string;
  colSpan: number;
}) {
  return (
    <div
      className="col-span-full border-b border-border px-4 py-6 text-center text-sm text-muted-foreground"
      style={{ gridColumn: `1 / span ${colSpan}` }}
    >
      {label}
    </div>
  );
}

// ============================================================
// Member row + cell
// ============================================================

interface MemberRowProps {
  member: AttendanceMember;
  role: 'athlete' | 'leader';
  columns: CalendarColumn[];
  merged: PendingMap;
  now: Date;
  rules: AttendanceSubsidy | undefined;
  onCellClick: (activityId: string, memberId: string) => void;
  t: TFunction<'common'>;
}

function MemberRow({
  member,
  role,
  columns,
  merged,
  now,
  rules,
  onCellClick,
  t,
}: MemberRowProps) {
  const rulesConfigured = !!rules && rules.kind !== '';
  return (
    <>
      <div className="sticky left-0 z-10 flex items-center gap-2 border-b border-border bg-card px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="block truncate text-sm font-medium leading-tight text-foreground">
            {member.first_name} {member.last_name}
          </span>
          {!member.birth_year ? (
            <span
              className="inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground"
              title={t('attendance.row.noBirthYear')}
              aria-label={t('attendance.row.noBirthYear')}
            >
              <Question weight="bold" className="size-3" />
            </span>
          ) : null}
          {member.birth_year ? (
            <span className="ml-auto text-[0.6875rem] tabular-nums text-muted-foreground">
              {rulesConfigured
                ? t('attendance.row.bornAndAge', {
                    year: member.birth_year,
                    age: currentAge(member.birth_year),
                  })
                : member.birth_year}
            </span>
          ) : null}
        </div>
        {rulesConfigured ? (
          <RowEligibilityDot
            member={member}
            role={role}
            rules={rules!}
            t={t}
          />
        ) : null}
      </div>
      {columns.map((col) => {
        if (col.kind === 'empty') {
          return (
            <div
              key={col.key}
              className={cn(
                'min-h-[2.75rem] border-b border-l border-border',
                INACTIVE_BG,
              )}
              aria-hidden
            />
          );
        }
        const a = col.activity;
        const key = cellKey(a.id, member.id);
        const state = merged[key] ?? '';
        const mode = computeColumnMode(a, now, a.confirmed_at ?? null);
        const start = new Date(a.starts_at);
        return (
          <AttendanceCell
            key={col.key}
            state={state}
            mode={mode}
            member={member}
            role={role}
            activityYear={start.getFullYear()}
            rules={rules}
            onClick={() => onCellClick(a.id, member.id)}
          />
        );
      })}
    </>
  );
}

function RowEligibilityDot({
  member,
  role,
  rules,
  t,
}: {
  member: AttendanceMember;
  role: 'athlete' | 'leader';
  rules: AttendanceSubsidy;
  t: TFunction<'common'>;
}) {
  const eligible =
    role === 'leader'
      ? isLeaderEligibleToday(member, rules)
      : isAthleteEligibleToday(member, rules);
  if (!member.birth_year) {
    return (
      <span
        className="inline-flex size-2 shrink-0 rounded-full bg-muted-foreground/40"
        title={t('attendance.row.dotNoDob')}
        aria-label={t('attendance.row.dotNoDob')}
      />
    );
  }
  return (
    <span
      className={cn(
        'inline-flex size-2 shrink-0 rounded-full',
        eligible ? 'bg-primary' : 'bg-border',
      )}
      title={
        eligible ? t('attendance.row.dotEligible') : t('attendance.row.dotIneligible')
      }
      aria-label={
        eligible ? t('attendance.row.dotEligible') : t('attendance.row.dotIneligible')
      }
    />
  );
}

interface AttendanceCellProps {
  state: CellState;
  mode: ColumnMode;
  member: AttendanceMember;
  role: 'athlete' | 'leader';
  activityYear: number;
  rules: AttendanceSubsidy | undefined;
  onClick: () => void;
}

function AttendanceCell({
  state,
  mode,
  member,
  role,
  activityYear,
  rules,
  onClick,
}: AttendanceCellProps) {
  // Three cell modes:
  //   • locked    — empty cell, non-interactive (activity > 60 min away).
  //   • editing   — checkbox, tap to toggle present/clear.
  //   • confirmed — "tell" glyph (lime ✓ counted, outline icon for too-
  //                 young / too-old / no-DOB, muted dot for absent).
  const rulesConfigured = !!rules && rules.kind !== '';
  const present = state === 'present';
  const tell =
    mode === 'confirmed' && rulesConfigured
      ? cellTell(state, member, role, activityYear, rules!)
      : null;

  if (mode === 'locked') {
    return (
      <div
        className="flex min-h-[2.75rem] items-center justify-center border-b border-l border-border bg-muted/10"
        aria-hidden
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={mode === 'confirmed'}
      className={cn(
        'flex min-h-[2.75rem] items-center justify-center border-b border-l border-border bg-card transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        mode === 'editing' && 'hover:bg-muted/40',
        mode === 'editing' && present && 'bg-primary/10',
        mode === 'confirmed' && 'cursor-default',
      )}
      aria-pressed={present}
    >
      {mode === 'editing' ? (
        <CheckboxGlyph checked={present} />
      ) : tell ? (
        <TellGlyph tell={tell} />
      ) : (
        <span className="block size-2 rounded-full bg-border/50" aria-hidden />
      )}
    </button>
  );
}

function CheckboxGlyph({ checked }: { checked: boolean }) {
  // Plain, large checkbox. Lime fill when checked so the brand color is
  // the affirmative signal in the grid.
  return checked ? (
    <span className="inline-flex size-6 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-xs">
      <Check className="size-4" weight="bold" />
    </span>
  ) : (
    <span className="inline-flex size-6 rounded-md border border-border bg-card" />
  );
}

type CellTell =
  | { kind: 'counts' }
  | { kind: 'tooYoung'; age: number; minAge: number }
  | { kind: 'tooOld'; age: number; maxAge: number }
  | { kind: 'leaderUnderage'; age: number; minAge: number }
  | { kind: 'noDob' }
  | { kind: 'absent' };

function cellTell(
  state: CellState,
  member: AttendanceMember,
  role: 'athlete' | 'leader',
  activityYear: number,
  rules: AttendanceSubsidy,
): CellTell {
  if (state !== 'present') return { kind: 'absent' };
  if (!member.birth_year) return { kind: 'noDob' };
  const age = activityYear - member.birth_year;
  if (role === 'leader') {
    if (age < rules.leader_min_age) {
      return { kind: 'leaderUnderage', age, minAge: rules.leader_min_age };
    }
    return { kind: 'counts' };
  }
  if (age < rules.min_age) return { kind: 'tooYoung', age, minAge: rules.min_age };
  if (age > rules.max_age) return { kind: 'tooOld', age, maxAge: rules.max_age };
  return { kind: 'counts' };
}

function TellGlyph({ tell }: { tell: CellTell }) {
  // Icons stay ink-coloured by default; only lime gets a color (positive
  // affirmation per the design system). The "doesn't count" states use
  // outline icons with semantic glyphs (baby = too young, cake = aging
  // out, question = unknown).
  switch (tell.kind) {
    case 'counts':
      return (
        <CheckCircle
          weight="fill"
          className="size-5 text-primary"
          aria-label="counts"
        />
      );
    case 'tooYoung':
      return (
        <span
          className="inline-flex size-5 items-center justify-center rounded-full border border-border text-foreground"
          title={`too young (age ${tell.age}, min ${tell.minAge})`}
        >
          <Baby weight="bold" className="size-3.5" />
        </span>
      );
    case 'tooOld':
      return (
        <span
          className="inline-flex size-5 items-center justify-center rounded-full border border-border text-foreground"
          title={`above age limit (age ${tell.age}, max ${tell.maxAge})`}
        >
          <Cake weight="bold" className="size-3.5" />
        </span>
      );
    case 'leaderUnderage':
      return (
        <span
          className="inline-flex size-5 items-center justify-center rounded-full border border-border text-foreground"
          title={`leader underage (age ${tell.age}, min ${tell.minAge})`}
        >
          <ClockCountdown weight="bold" className="size-3.5" />
        </span>
      );
    case 'noDob':
      return (
        <span
          className="inline-flex size-5 items-center justify-center rounded-full border border-border text-muted-foreground"
          title="birth date missing"
        >
          <Question weight="bold" className="size-3.5" />
        </span>
      );
    case 'absent':
    default:
      return (
        <span className="block size-2 rounded-full bg-border/50" aria-hidden />
      );
  }
}

// ============================================================
// Issue icon (used in summary card + breakdown)
// ============================================================

function IssueIcon({ issue, className }: { issue: string; className?: string }) {
  switch (issue) {
    case 'too_short':
      return <ClockCountdown weight="bold" className={className} />;
    case 'not_enough_eligible':
      return <UsersThree weight="bold" className={className} />;
    case 'no_leader':
      return <UserCircle weight="bold" className={className} />;
    case 'leader_underage':
      return <Baby weight="bold" className={className} />;
    case 'too_many_per_leader':
      return <UsersThree weight="bold" className={className} />;
    default:
      return <WarningCircle weight="bold" className={className} />;
  }
}

// ============================================================
// Pure helpers
// ============================================================

// computeColumnMode decides whether a column renders as locked (registration
// not yet open), editing (checkboxes that auto-save on click), or confirmed
// (subsidy tells, non-interactive). The backend confirmation timestamp is
// authoritative: a confirmed activity is locked regardless of timing — its
// marks were finalised for subsidy reporting and can't change until reopened.
// Otherwise the column is editable once its registration window has opened.
function computeColumnMode(
  activity: AttendanceActivity,
  now: Date,
  confirmedAt: string | null,
): ColumnMode {
  if (confirmedAt) return 'confirmed';
  const startMs = new Date(activity.starts_at).getTime();
  if (now.getTime() < startMs - REGISTRATION_OPEN_MS) return 'locked';
  return 'editing';
}

function computeColumnVerdict(
  rollup: AttendanceRollup | undefined,
  hasRecords: boolean,
): 'qualifies' | 'issues' | 'blank' {
  if (!rollup || !hasRecords) return 'blank';
  if (rollup.qualifies) return 'qualifies';
  return 'issues';
}

function isAthleteEligibleToday(
  m: AttendanceMember,
  rules: AttendanceSubsidy,
): boolean {
  if (!m.birth_year) return false;
  const age = new Date().getFullYear() - m.birth_year;
  return age >= rules.min_age && age <= rules.max_age;
}

function isLeaderEligibleToday(
  m: AttendanceMember,
  rules: AttendanceSubsidy,
): boolean {
  if (!m.birth_year) return false;
  return new Date().getFullYear() - m.birth_year >= rules.leader_min_age;
}

function currentAge(birthYear: number): number {
  return new Date().getFullYear() - birthYear;
}

function parseISODate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

// windowRange returns the fetch window: first of the anchor's month
// (inclusive) → WINDOW_DAYS later (exclusive). Local day components keep
// the boundaries in the user's calendar. The result is paginated
// client-side, so the exact end boundary isn't user-visible.
function windowRange(anchor: Date): { from: string; to: string } {
  const from = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const to = new Date(from);
  to.setDate(to.getDate() + WINDOW_DAYS);
  return { from: dayKey(from), to: dayKey(to) };
}

// dayKey returns a local-time YYYY-MM-DD string so day-comparisons stay
// in the user's calendar (the API returns full ISO timestamps; rendering
// against UTC would shift midnight-ish activities by a day in CET).
function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// buildCalendarColumns takes the page slice of (already-sorted)
// activities, packs them into columns left-to-right with no day gaps,
// then pads with greyed `empty` columns out to pageSize so the page
// always fills the container width. Same-day activities cluster with the
// date label suppressed on the 2nd+ column so the day reads once.
function buildCalendarColumns(
  sortedActivities: AttendanceActivity[],
  page: number,
  pageSize: number,
): CalendarColumn[] {
  const start = page * pageSize;
  const pageActivities = sortedActivities.slice(start, start + pageSize);

  const cols: CalendarColumn[] = [];
  let prevDayKey: string | null = null;
  for (const a of pageActivities) {
    const date = new Date(a.starts_at);
    const k = dayKey(date);
    cols.push({
      kind: 'activity',
      date,
      activity: a,
      showDate: k !== prevDayKey,
      key: `act-${a.id}`,
    });
    prevDayKey = k;
  }

  const padding = Math.max(0, pageSize - cols.length);
  for (let i = 0; i < padding; i += 1) {
    cols.push({ kind: 'empty', key: `pad-${i}` });
  }
  return cols;
}

interface MonthBand {
  label: string;
  span: number;
  key: string;
}

// buildMonthBands groups consecutive activity columns by their start
// month for the top header row. Padding columns at the tail collapse
// into a single un-labelled span so they look like neutral table space.
function buildMonthBands(
  cols: CalendarColumn[],
  locale: string,
): MonthBand[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'short' });
  const bands: MonthBand[] = [];
  for (const col of cols) {
    if (col.kind === 'empty') {
      const last = bands[bands.length - 1];
      if (last && last.key === 'padding') {
        last.span += 1;
      } else {
        bands.push({ label: '', span: 1, key: 'padding' });
      }
      continue;
    }
    const label = fmt.format(col.date).toUpperCase();
    const key = `${col.date.getFullYear()}-${col.date.getMonth()}`;
    const last = bands[bands.length - 1];
    if (last && last.key === key) {
      last.span += 1;
    } else {
      bands.push({ label, span: 1, key });
    }
  }
  return bands;
}

